import { ReactElement } from 'react';
import { EnvelopeIcon } from '@heroicons/react/24/outline';
import { useMutation } from '@apollo/client/react';
import { Input, Modal, ModalFooter } from '@sc/shared-ui';
import { CompanyUserRole } from '@sc/graphql';
import {
  GQL_MUTATION_CREATE_INVITATION_FOR_USER,
  GQL_QUERY_INDEX_USERS,
} from '../utility/queries';
import { useAlerts } from '../providers/alerts';
import { notify } from '../utility/notify';
import { track } from '../utility/analytics';
import { usersUtility } from '../utility/users';
import { Tile } from '../components/Tile';
import { AccountDetailsGate } from '../components/AccountDetailsGate';
import { useForm } from '@tanstack/react-form';

export const InviteUserModal = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}): ReactElement => {
  const { useApolloError } = useAlerts();
  const onError = useApolloError();

  const [createInvitationForUser, { loading }] = useMutation(
    GQL_MUTATION_CREATE_INVITATION_FOR_USER
  );

  const form = useForm({
    defaultValues: {
      email: '',
      role: CompanyUserRole.member,
    },
    onSubmit: ({ value }) => {
      void createInvitationForUser({
        variables: { input: { email: value.email, role: value.role } },
        refetchQueries: [GQL_QUERY_INDEX_USERS],
        onCompleted: () => {
          track('User Invited', { role: value.role });

          notify.success('Invitation sent');

          onClose();
        },
        onError,
      });
    },
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Invite member"
      dialogPanelClassname="w-[520px]"
      loading={loading}
    >
      <AccountDetailsGate description="First, add your details — they appear on the invitation email.">
      <form onSubmit={(e) => {
        e.preventDefault();

        void form.handleSubmit();
      }}>
        <div className="p-5 space-y-5">
          <form.Field
            children={({ state, handleChange }) => (
              <Input
                label="Member email"
                type="email"
                Icon={EnvelopeIcon}
                value={state.value}
                required
                onChange={(e) => handleChange(e.target.value)}
              />
            )}
            name="email"
          />
          <div className="space-y-2">
            <span className="text-body-medium font-semibold block">
              Member access level
            </span>
            <form.Field
              children={({ state, handleChange }) => (
                <div className="flex gap-2">
                  {usersUtility.roleTiles.map((tile) => (
                    <Tile
                      key={tile.role}
                      Icon={tile.Icon}
                      label={usersUtility.companyUserRoleDisplayTitles[tile.role]}
                      description={tile.description}
                      checked={state.value === tile.role}
                      onChange={() => handleChange(tile.role)}
                    />
                  ))}
                </div>
              )}
              name="role"
            />
          </div>
        </div>
        <ModalFooter
          asForm
          buttonsDisabled={loading}
          onClose={onClose}
          submitText="Send invite"
        />
      </form>
      </AccountDetailsGate>
    </Modal>
  );
};
